/**
 * The full review screen — one scrollable surface, Midnight Gold theme, in the locked order:
 * header → hero → rating → basics → voice → price/service → vibe/good-for/party/when/back →
 * expandable deep sections → photo+link → sticky submit bar.
 * @see docs/05-CLAUDE-CODE-BUILD-BRIEF.md §3  @see docs/pennysaver-review-architecture-S73.md
 */
import {
  BASICS, PRICING, VALUE_SCALE, SERVICE, VIBES, VIBE_MAX, GOOD_FOR, GOOD_FOR_MAX,
  PARTY, WHEN, BACK, DEEP,
} from '@pghbites/shared';
import { canPostRating, canPostReview, type ReviewFormApi, type MultiField } from '../state/useReviewForm.js';
import { Header } from './Header.js';
import { Hero } from './Hero.js';
import { RiverRating } from './RiverRating.js';
import { WaveGlyph } from './WaveGlyph.js';
import { TextField } from './TextField.js';
import { VoicePicker } from './VoicePicker.js';
import { SingleSelect } from './SingleSelect.js';
import { MultiSelect } from './MultiSelect.js';
import { ExpandableSection } from './ExpandableSection.js';
import { PhotoLink } from './PhotoLink.js';
import { SubmitBar } from './SubmitBar.js';
import { LegalFooter } from './LegalFooter.js';

const MULTILINE = new Set(['ordered', 'anything']);

interface ReviewScreenProps {
  form: ReviewFormApi;
  busy: boolean;
  error?: string | null;
  onPostRating: () => void;
  onPostReview: () => void;
}

export function ReviewScreen({ form, busy, error, onPostRating, onPostReview }: ReviewScreenProps) {
  const { review } = form;
  const canRating = canPostRating(review);
  const canReview = canPostReview(review);

  return (
    <div className="screen">
      <div className="screen-scroll">
        <Header />

        <Hero
          restaurant={review.restaurant}
          neighborhood={review.neighborhood ?? ''}
          onName={form.setRestaurantName}
          onSelectPlace={form.selectPlace}
        />

        <section className="block">
          <h2 className="block-title block-title-rivers">
            <WaveGlyph count={2} inline />
            Rating
          </h2>
          <p className="block-hint">Just a rating is a complete review. Tap one.</p>
          <RiverRating value={review.rating} onChange={form.setRating} />
        </section>

        <section className="block">
          <h2 className="block-title">The basics</h2>
          {BASICS.map((b) => (
            <TextField
              key={b.key}
              id={`basic-${b.key}`}
              label={b.label}
              value={(review[b.key] as string | undefined) ?? ''}
              placeholder={b.hint}
              required={b.required}
              multiline={MULTILINE.has(b.key)}
              onChange={(v) => form.setText(b.key, v)}
            />
          ))}
        </section>

        <section className="block">
          <VoicePicker value={review.voice} onChange={form.setVoice} />
        </section>

        <section className="block">
          <h2 className="block-title">Pricing</h2>
          <SingleSelect options={PRICING} value={review.pricing} ariaLabel="Pricing" onChange={(v) => form.setSingle('pricing', v)} />
          <h2 className="block-title">Value</h2>
          <SingleSelect options={VALUE_SCALE} value={review.worth} ariaLabel="Value" onChange={(v) => form.setSingle('worth', v)} />
          <h2 className="block-title">Service</h2>
          <SingleSelect options={SERVICE} value={review.service} ariaLabel="Service" onChange={(v) => form.setSingle('service', v)} />
        </section>

        <section className="block">
          <h2 className="block-title">Vibe <span className="block-cap">pick up to {VIBE_MAX}</span></h2>
          <MultiSelect
            options={VIBES}
            selected={review.vibe ?? []}
            max={VIBE_MAX}
            ariaLabel="Vibe"
            onToggle={(v) => form.toggleMulti('vibe', v, VIBE_MAX)}
            onAddCustom={(v) => form.addCustomCapped('vibe', v, VIBE_MAX)}
          />

          <h2 className="block-title">Good for <span className="block-cap">pick up to {GOOD_FOR_MAX}</span></h2>
          <MultiSelect
            options={GOOD_FOR}
            selected={review.good_for ?? []}
            max={GOOD_FOR_MAX}
            ariaLabel="Good for"
            onToggle={(v) => form.toggleMulti('good_for', v, GOOD_FOR_MAX)}
            onAddCustom={(v) => form.addCustomCapped('good_for', v, GOOD_FOR_MAX)}
          />

          <h2 className="block-title">Party size</h2>
          <SingleSelect options={PARTY} value={review.party} ariaLabel="Party size" onChange={(v) => form.setSingle('party', v)} />

          <h2 className="block-title">When you went</h2>
          <SingleSelect options={WHEN} value={review.when} ariaLabel="When you went" onChange={(v) => form.setSingle('when', v)} />

          <h2 className="block-title">Would you go back?</h2>
          <SingleSelect options={BACK} value={review.back} ariaLabel="Would you go back?" onChange={(v) => form.setSingle('back', v)} />
        </section>

        <section className="block">
          <h2 className="block-title">Wanna leave more?</h2>
          <p className="block-hint">All optional. Tap all that fit, or add your own.</p>
          {DEEP.map((cat) => (
            <ExpandableSection
              key={cat.key}
              title={cat.title}
              options={cat.options}
              selected={(review[cat.key as MultiField] as string[] | undefined) ?? []}
              onToggle={(v) => form.toggleMulti(cat.key as MultiField, v, 10)}
              onAddCustom={(v) => form.addCustom(cat.key as MultiField, v)}
            />
          ))}
        </section>

        <section className="block">
          <h2 className="block-title">Photo &amp; link</h2>
          <PhotoLink
            photoUrl={review.photo_url ?? ''}
            link={review.link ?? ''}
            onPhotoUrl={(v) => form.setText('photo_url', v)}
            onLink={(v) => form.setText('link', v)}
          />
        </section>

        {error && (
          <p className="error-banner" role="alert">
            {error}
          </p>
        )}

        <LegalFooter showAcceptance />

        <div className="screen-spacer" />
      </div>

      <SubmitBar
        canPostRating={canRating}
        canPostReview={canReview}
        busy={busy}
        onPostRating={onPostRating}
        onPostReview={onPostReview}
      />
    </div>
  );
}
